import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { SkillIcon } from 'atoms/SkillIcon/SkillIcon';
import { ICareerSkillEdge, ICareerSkillNode } from 'core/hooks/CareerSkillGraph';

import { ISkillGraphIndex } from '../../model/skillGraphModel';
import { Block, BlockTitle, Chip, Chips, Meta } from './passportStyles';

/**
 * Паспорт, пункт 7 ТЗ 0071 (п. 6.4): предпосылки и следующие навыки.
 *
 * 🔴 Связи разложены ПО СМЫСЛУ, а не одним списком. Прежние «Najpierw
 * potrzebne» и «Otwiera» склеивали обязательную основу и рекомендуемый порядок
 * — а это разные утверждения: без первой навык не осваивается, второй только
 * удобнее. Смысл связи назван словами рядом с чипами; на холсте он же
 * передаётся штрихом линии, но в паспорте линий нет.
 */
interface ISkillPassportLinks {
  index: ISkillGraphIndex;
  node: ICareerSkillNode;
  onOpenSkill: (slug: string) => void;
}

type EdgeKind = ICareerSkillEdge['kind'];

/** Порядок и подписи групп — словарём литералов, как везде в паспорте. */
const KINDS: { kind: EdgeKind; labelKey: string }[] = [
  { kind: 'required', labelKey: 'careerCenter.map.links.required' },
  { kind: 'recommended', labelKey: 'careerCenter.map.links.recommended' },
];

export const SkillPassportLinks = memo(({ index, node, onOpenSkill }: ISkillPassportLinks) => {
  const { t } = useTranslation();

  const before = (index.needsIn.get(node.id) || []).map((edge) => ({
    kind: edge.kind,
    node: index.byId.get(edge.from),
  }));
  const after = (index.opensOut.get(node.id) || []).map((edge) => ({
    kind: edge.kind,
    node: index.byId.get(edge.to),
  }));

  const renderSide = (
    rows: { kind: EdgeKind; node: ICareerSkillNode | undefined }[],
    titleId: string,
    titleKey: string,
  ) => {
    if (!rows.some((row) => row.node)) return null;
    return (
      <Block aria-labelledby={titleId}>
        <BlockTitle id={titleId}>{t(titleKey)}</BlockTitle>
        {KINDS.map(({ kind, labelKey }) => {
          const nodes = rows
            .filter((row) => row.kind === kind)
            .map((row) => row.node)
            .filter((row): row is ICareerSkillNode => Boolean(row));
          if (!nodes.length) return null;
          return (
            <React.Fragment key={kind}>
              <Meta>{t(labelKey)}</Meta>
              <Chips>
                {nodes.map((row) => (
                  <Chip key={row.id} type="button" onClick={() => onOpenSkill(row.slug)}>
                    <SkillIcon
                      slug={row.slug}
                      name={row.name}
                      category={row.category}
                      iconUrl={row.iconUrl}
                      size={16}
                    />
                    {row.name}
                  </Chip>
                ))}
              </Chips>
            </React.Fragment>
          );
        })}
      </Block>
    );
  };

  return (
    <>
      {renderSide(before, 'skm-passport-before', 'careerCenter.map.links.before')}
      {renderSide(after, 'skm-passport-after', 'careerCenter.map.links.after')}
    </>
  );
});
